import { useState } from 'react';
import { Lock, Shield, AlertTriangle, CheckCircle2, Download, Clock, FileText, Search, Sliders, XCircle } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { exportPopulationCSV, exportExceptionReportPDF, exportInternalAuditPackage, exportAuditPackageZIP, exportIntegrityManifestPDF } from '@/utils/exportUtils';
import GradeTooltip from '@/components/GradeTooltip';

const integrityEvents = [
  { id: 'DER-2026-047832', time: 'Apr 15, 14:32', event: 'Signature Verified', actor: 'contract-processor@meridian.global', status: 'verified' },
  { id: 'DER-2026-047831', time: 'Apr 15, 14:31', event: 'Signature Verified', actor: 'legaldocs-agent@meridian.global', status: 'verified' },
  { id: 'DER-2026-031204', time: 'Apr 15, 11:14', event: 'Hash Mismatch Warning', actor: 'system', status: 'warning' },
  { id: 'DER-2026-047785', time: 'Apr 15, 13:28', event: 'Record Accessed', actor: 'audit-review@meridian.global', status: 'logged' },
  { id: 'DER-2026-047750', time: 'Apr 15, 12:55', event: 'Export Generated', actor: 'compliance-team@meridian.global', status: 'logged' },
];

const retentionPolicies = [
  { framework: 'EU AI Act', years: 7, records: 31204, nextExpiry: 'Jan 2028', color: '#6366F1' },
  { framework: 'ISO 42001', years: 5, records: 11847, nextExpiry: 'Jun 2027', color: '#3B82F6' },
  { framework: 'NIST AI RMF', years: 3, records: 4781, nextExpiry: 'Dec 2026', color: '#10B981' },
];

const frameworkBars = [
  { name: 'EU AI Act', count: 31204, max: 47832, color: '#6366F1' },
  { name: 'ISO 42001', count: 11847, max: 47832, color: '#3B82F6' },
  { name: 'NIST AI RMF', count: 4781, max: 47832, color: '#10B981' },
];

const controlEvidenceMap = [
  { id: 'CC6.1', framework: 'SOC 2', desc: 'Logical access controls for AI systems', source: 'Agent Controls + Decision Ledger', records: '47,832', coverage: 100, status: 'ready', proofGrade: 'AUDIT-GRADE' },
  { id: 'PI1.1', framework: 'SOC 2', desc: 'Processing integrity of AI outputs', source: 'Decision Ledger', records: '47,832', coverage: 94, status: 'partial', proofGrade: 'AUDIT-GRADE' },
  { id: 'PI1.2', framework: 'SOC 2', desc: 'Complete and accurate AI output recording', source: 'Evidence Vault', records: '47,614', coverage: 99.5, status: 'ready', proofGrade: 'AUDIT-GRADE' },
  { id: 'CM-1', framework: 'SOC 2', desc: 'Change management for AI systems', source: 'Change Control', records: '14 changes', coverage: 93, status: 'ready', proofGrade: 'AUDIT-GRADE' },
  { id: 'IR-1', framework: 'SOC 2', desc: 'Incident response for AI events', source: 'Incident Review', records: '1 incident', coverage: 100, status: 'ready', proofGrade: 'AUDIT-GRADE' },
  { id: 'ISO-6.1', framework: 'ISO 42001', desc: 'AI risk assessment documentation', source: 'Compliance Gap Register', records: '\u2014', coverage: 55, status: 'partial', proofGrade: 'OBSERVATIONAL' },
  { id: 'EU-AI-13.1', framework: 'EU AI Act', desc: 'Transparency documentation', source: 'Decision Ledger + Roles', records: '8,203', coverage: 68, status: 'gap', proofGrade: 'OPERATIONS-GRADE' },
];

const allWorkflows = ['Contract Classification', 'Legal Document Drafting', 'Academy Recommendations', 'Webcast Metadata', 'Claims Document Processing'];

function HashMismatchDetail({ onClose }) {
  return (
    <div className="glass-card p-5 border-l-3" style={{ borderLeftWidth: 3, borderLeftColor: '#F59E0B' }}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-[#F59E0B]" />
          <span className="text-sm font-semibold text-[#F8FAFC]">Hash Mismatch — DER-2026-031204</span>
        </div>
        <button onClick={onClose} className="text-[#475569] hover:text-[#F8FAFC] text-xs">Close</button>
      </div>
      <div className="space-y-3 text-xs text-[#94A3B8]">
        <p>A hash integrity check failed for record DER-2026-031204 during the scheduled verification sweep at 11:14 UTC.</p>
        <div className="p-3 rounded-lg bg-[#080D1A] border border-[#1E2A45] font-mono text-[10px]">
          <div><span className="text-[#475569]">Expected:</span> sha256:f2a19c8b...d4e7</div>
          <div><span className="text-[#475569]">Computed:</span> sha256:f2a19c8b...d4e8</div>
          <div className="text-[#F59E0B] mt-1">Single-bit divergence in trailing checksum block</div>
        </div>
        <p>Root cause analysis: Storage subsystem bit-flip during replication. No evidence of tampering.</p>
        <div className="flex gap-2 mt-3">
          <button className="bg-[#6366F1] hover:bg-[#818cf8] text-white text-xs px-3 py-1.5 rounded-lg transition-all" onClick={() => toast.success('Re-verification initiated for DER-2026-031204')}>Re-verify & Repair</button>
          <button className="bg-[#0F1629] border border-[#1E2A45] text-[#94A3B8] text-xs px-3 py-1.5 rounded-lg" onClick={() => toast.info('Escalated to IT Security team')}>Escalate to Security</button>
        </div>
      </div>
    </div>
  );
}

function parseQuery(text) {
  const t = text.trim();
  if (!t) return null;
  if (t.startsWith('grade:')) return { type: 'grade', value: t.slice(6).trim() };
  if (t.startsWith('model:')) return { type: 'model', value: t.slice(6).trim() };
  if (t.startsWith('workflow:')) return { type: 'workflow', value: t.slice(9).trim() };
  if (t.startsWith('sha256:') || t.startsWith('DER-')) return { type: 'id', value: t };
  return null;
}

function VaultOverview() {
  const [showMismatchDetail, setShowMismatchDetail] = useState(false);
  const [proofGradeFilter, setProofGradeFilter] = useState('all');
  const [reconFilter, setReconFilter] = useState('all');
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchText, setSearchText] = useState('');
  const parsedQuery = parseQuery(searchText);

  // Effective filters: parsed grade overrides dropdown if present
  const effectiveGradeFilter = (() => {
    if (parsedQuery && parsedQuery.type === 'grade') {
      const v = parsedQuery.value.toUpperCase();
      if (v.startsWith('AUDIT')) return 'audit';
      if (v.startsWith('OPS') || v.startsWith('OPERATIONS')) return 'ops';
      if (v.startsWith('OBS')) return 'obs';
    }
    return proofGradeFilter;
  })();
  const filterActive = effectiveGradeFilter !== 'all' || reconFilter !== 'all' || parsedQuery !== null;
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select data-testid="proof-grade-filter" value={effectiveGradeFilter} onChange={e => { setProofGradeFilter(e.target.value); setSearchText(''); }}
          className="text-xs bg-[#0F1629] border border-[#1E2A45] text-[#94A3B8] rounded-lg px-3 py-2 focus:outline-none focus:border-[#6366F1]">
          <option value="all">All Proof Grades</option>
          <option value="audit">AUDIT-GRADE only</option>
          <option value="ops">OPERATIONS-GRADE</option>
          <option value="obs">OBSERVATIONAL</option>
        </select>
        <select data-testid="reconstruction-filter" value={reconFilter} onChange={e => setReconFilter(e.target.value)}
          className="text-xs bg-[#0F1629] border border-[#1E2A45] text-[#94A3B8] rounded-lg px-3 py-2 focus:outline-none focus:border-[#6366F1]">
          <option value="all">All Reconstruction Methods</option>
          <option value="sdk">SDK Capture</option>
          <option value="log">Log Reconstruction</option>
          <option value="provider">Provider Log</option>
          <option value="siem">SIEM Enrichment</option>
          <option value="grc">GRC Import</option>
          <option value="manual">Manual Attestation</option>
        </select>
        <div className="text-[10px] text-[#64748B]" data-testid="filter-indicator">
          {filterActive ? (parsedQuery ? `Query filter active · ${parsedQuery.type}:${parsedQuery.value}` : 'Filtered view active') : '47,832 records'}
        </div>
        <div className="text-[9px] text-[#475569] font-mono ml-auto">Evidence fabric managed by Proof Graph Service · 5 active workflows</div>
      </div>

      {/* Evidence fabric search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
        <input
          type="text"
          data-testid="evidence-fabric-search"
          placeholder="Query evidence fabric · Proof Graph Service..."
          value={searchText}
          className="w-full pl-10 pr-4 py-2 bg-[#080D1A] border border-[#1E2A45] rounded-lg text-xs text-[#F8FAFC] placeholder:text-[#475569] focus:outline-none focus:border-[#6366F1]"
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
          onChange={(e) => { setSearchText(e.target.value); if (e.target.value.length > 0) setSearchFocused(false); }}
        />
        {searchFocused && (
          <div data-testid="proof-graph-query-hints"
            className="absolute top-full left-0 right-0 mt-1 rounded-lg bg-[#080D1A]/95 backdrop-blur-sm border border-[#1E2A45] p-3 z-20 shadow-lg">
            <div className="text-[7px] uppercase tracking-wider text-[#475569] font-semibold mb-2">PROOF GRAPH SERVICE · Query examples</div>
            <div className="space-y-1 font-mono text-[8px] text-[#475569]">
              <div className="flex items-center justify-between"><span className="text-[#94A3B8]">sha256:a4f2c...</span><span>hash lookup</span></div>
              <div className="flex items-center justify-between"><span className="text-[#94A3B8]">DER-2026-</span><span>record prefix</span></div>
              <div className="flex items-center justify-between"><span className="text-[#94A3B8]">model:gpt-4o</span><span>model filter</span></div>
              <div className="flex items-center justify-between"><span className="text-[#94A3B8]">workflow:legal</span><span>workflow filter</span></div>
              <div className="flex items-center justify-between"><span className="text-[#94A3B8]">grade:AUDIT-GRADE</span><span>grade filter</span></div>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Total Records</div><div className="text-2xl font-bold text-[#F8FAFC]">47,832</div></div>
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Verified & Signed</div><div className="text-2xl font-bold text-[#10B981]">47,614</div></div>
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Integrity Warnings</div><div className="text-2xl font-bold text-[#F59E0B]">218</div></div>
        <div className="glass-card p-4 relative group" data-testid="sdk-captured-stat">
          <div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1 flex items-center gap-1">
            SDK Captured
            <span className="text-[#475569] cursor-help">ⓘ</span>
          </div>
          <div className="text-2xl font-bold text-[#F8FAFC]">47,421</div>
          <div className="text-[10px] text-[#10B981]">99.1%</div>
          <div className="absolute z-30 top-full left-0 right-0 mt-1 hidden group-hover:block bg-[#0F1629] border border-[#1E2A45] rounded-lg p-3 text-[10px] text-[#94A3B8] leading-relaxed shadow-xl">
            Records captured via in-line SDK wrapper at invocation time — the most complete capture method. 411 records were reconstructed from logs or enriched from external sources.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="glass-card p-6">
            <h3 className="text-xs font-semibold text-[#F8FAFC] uppercase tracking-wider mb-4">Storage by Framework</h3>
            <div className="space-y-3">
              {frameworkBars.map((fb) => (
                <div key={fb.name} className="flex items-center gap-3">
                  <span className="text-xs text-[#94A3B8] w-24 flex-shrink-0">{fb.name}</span>
                  <div className="flex-1 h-2 rounded-full bg-[#1E2A45] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(fb.count / fb.max) * 100}%`, backgroundColor: fb.color }} />
                  </div>
                  <span className="text-xs font-mono text-[#94A3B8] w-16 text-right">{fb.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 rounded-lg bg-[#10B981]/5 border border-[#10B981]/20 flex items-center gap-2">
              <CheckCircle2 size={14} className="text-[#10B981]" />
              <span className="text-xs text-[#10B981]">Last integrity check: 4 minutes ago — All hashes verified.</span>
            </div>
          </div>

          <div className="glass-card p-6">
            <h3 className="text-xs font-semibold text-[#F8FAFC] uppercase tracking-wider mb-4">Recent Integrity Events</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1E2A45]">
                  {['Record ID', 'Timestamp', 'Event', 'Actor', 'Status'].map((h) => (
                    <th key={h} className="text-left text-[10px] font-medium text-[#475569] uppercase tracking-wider py-2 px-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {integrityEvents.map((ev) => (
                  <tr key={ev.id + ev.event} data-testid={`integrity-event-${ev.id}`}
                    onClick={() => ev.status === 'warning' && setShowMismatchDetail(true)}
                    className={`border-b border-[#1E2A45]/50 ${ev.status === 'warning' ? 'table-row-interactive' : ''}`}>
                    <td className="py-2.5 px-3 font-mono text-[#6366F1] text-xs">{ev.id}</td>
                    <td className="py-2.5 px-3 font-mono text-[#94A3B8] text-xs">{ev.time}</td>
                    <td className="py-2.5 px-3 text-xs text-[#F8FAFC]">{ev.event}</td>
                    <td className="py-2.5 px-3 font-mono text-[#64748B] text-[11px]">{ev.actor}</td>
                    <td className="py-2.5 px-3">
                      <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${
                        ev.status === 'verified' ? 'bg-[#10B981]/10 text-[#10B981]' : ev.status === 'warning' ? 'bg-[#F59E0B]/10 text-[#F59E0B]' : 'bg-[#3B82F6]/10 text-[#3B82F6]'
                      }`}>
                        {ev.status === 'verified' ? '\u2713 Verified' : ev.status === 'warning' ? '\u26A0 Warning' : '\u2713 Logged'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {showMismatchDetail && <div className="mt-4"><HashMismatchDetail onClose={() => setShowMismatchDetail(false)} /></div>}
          </div>
        </div>

        <div className="space-y-4">
          {retentionPolicies.map((p) => (
            <div key={p.framework} className="glass-card p-5" data-testid={`retention-${p.framework}`}>
              <div className="flex items-center gap-2 mb-3">
                <Lock size={14} style={{ color: p.color }} />
                <span className="text-sm font-semibold text-[#F8FAFC]">{p.framework}</span>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between"><span className="text-[#64748B]">Retention</span><span className="text-[#F8FAFC] font-medium">{p.years} years</span></div>
                <div className="flex justify-between"><span className="text-[#64748B]">Records</span><span className="text-[#94A3B8] font-mono">{p.records.toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-[#64748B]">Next expiry</span><span className="text-[#94A3B8]">{p.nextExpiry}</span></div>
              </div>
            </div>
          ))}
          <div className="glass-card p-5">
            <h4 className="text-xs font-semibold text-[#F8FAFC] uppercase tracking-wider mb-3">Integrity Manifest</h4>
            <div className="text-[10px] text-[#64748B] mb-3">
              Last generated: Apr 14, 2026 · 14:00 UTC<br />
              <span className="font-mono text-[#475569]">sha256:f2a19c...</span>
            </div>
            <button data-testid="generate-manifest" onClick={() => { exportIntegrityManifestPDF(); toast.success('Integrity manifest downloaded as PDF'); }} className="w-full flex items-center justify-center gap-2 bg-[#6366F1] hover:bg-[#818cf8] text-white text-xs font-medium px-4 py-2.5 rounded-lg transition-all shadow-[0_0_8px_rgba(99,102,241,0.15)]">
              <Download size={13} /> Generate Integrity Manifest
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const extendedControlRows = [
  { id: 'ISO-A.12.4', framework: 'ISO 27001', desc: 'Logging and monitoring of AI decisions', source: 'Evidence Vault + Decision Ledger', records: '47,832', coverage: 96, status: 'ready' },
  { id: 'ISO-A.9.4', framework: 'ISO 27001', desc: 'Access control for AI systems', source: 'Agent Controls', records: '24 permissions', coverage: 100, status: 'ready' },
  { id: 'ISO-A.8.1', framework: 'ISO 27701', desc: 'Privacy controls for AI-processed PII', source: 'Data Access Records', records: '47,832', coverage: 88, status: 'partial' },
  { id: 'AU-2', framework: 'NIST 800-53', desc: 'Audit events for AI system invocations', source: 'Evidence Vault', records: '47,832', coverage: 99, status: 'ready' },
  { id: 'AC-6', framework: 'NIST 800-53', desc: 'Least privilege for AI agent permissions', source: 'Agent Controls', records: '6 agents', coverage: 92, status: 'ready' },
  { id: 'CM-3', framework: 'NIST 800-53', desc: 'Change control for AI model and config changes', source: 'Change Control', records: '14 changes', coverage: 93, status: 'ready' },
  { id: 'IR-4', framework: 'NIST 800-53', desc: 'Incident handling for AI events', source: 'Incident Review', records: '1 incident', coverage: 100, status: 'ready' },
  { id: 'NIS2-IR', framework: 'NIS2', desc: 'Incident reporting for AI-related events', source: 'Incident Review', records: '1 incident', coverage: 100, status: 'ready' },
  { id: 'NIS2-RM', framework: 'NIS2', desc: 'Risk management measures for AI systems', source: 'Risk Score + Compliance', records: '\u2014', coverage: 71, status: 'partial' },
  { id: 'NIS2-LOG', framework: 'NIS2', desc: 'Logging and monitoring obligations', source: 'Evidence Vault', records: '47,832', coverage: 96, status: 'ready' },
];

function AuditorWorkspace() {
  const [scopeGenerated, setScopeGenerated] = useState(false);
  const [frameworks, setFrameworks] = useState({ soc2: true, iso42001: true, euAiAct: true, nist: false, internal: false });
  const [extFrameworks, setExtFrameworks] = useState({ iso27001: false, nist80053: false, nis2: false });
  const [workflows, setWorkflows] = useState(allWorkflows.reduce((a, w) => ({ ...a, [w]: true }), {}));
  const [threshold, setThreshold] = useState(80);

  const toggleFramework = (key) => setFrameworks(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleExtFramework = (key) => setExtFrameworks(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleWorkflow = (key) => setWorkflows(prev => ({ ...prev, [key]: !prev[key] }));

  const hasExtended = extFrameworks.iso27001 || extFrameworks.nist80053 || extFrameworks.nis2;
  const activeExtRows = extendedControlRows.filter(r => {
    if (r.framework === 'ISO 27001' || r.framework === 'ISO 27701') return extFrameworks.iso27001;
    if (r.framework === 'NIST 800-53') return extFrameworks.nist80053;
    if (r.framework === 'NIS2') return extFrameworks.nis2;
    return false;
  });

  return (
    <div className="space-y-4">
      {/* Internal Audit framing header */}
      <div>
        <h2 className="text-base font-semibold text-[#F8FAFC] mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Internal Audit · SOC 2 · ISO 42001 · Insurance Underwriting</h2>
        <div className="text-xs text-[#64748B]">Apex is the AI evidence layer for your assurance programs. Define scope, generate populations, map controls to evidence, and export structured packages your auditors can test.</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" style={{ minHeight: 520 }}>
        {/* Left — Audit Scope Builder */}
        <div className="lg:col-span-2 glass-card p-5 space-y-5" data-testid="audit-scope-builder">
          <h3 className="text-xs font-semibold text-[#F8FAFC] uppercase tracking-wider">Define audit scope</h3>

          {/* Examination period */}
          <div>
            <div className="text-[10px] text-[#475569] uppercase tracking-wider mb-2">Examination period</div>
            <div className="flex gap-2">
              <input type="date" defaultValue="2026-01-01" className="flex-1 px-3 py-2 bg-[#080D1A] border border-[#1E2A45] rounded-lg text-xs text-[#F8FAFC] focus:outline-none focus:border-[#6366F1]" />
              <input type="date" defaultValue="2026-04-15" className="flex-1 px-3 py-2 bg-[#080D1A] border border-[#1E2A45] rounded-lg text-xs text-[#F8FAFC] focus:outline-none focus:border-[#6366F1]" />
            </div>
          </div>

          {/* Frameworks in scope */}
          <div>
            <div className="text-[10px] text-[#475569] uppercase tracking-wider mb-2">Frameworks in scope</div>
            <div className="space-y-1.5">
              {[
                { key: 'soc2', label: 'SOC 2' },
                { key: 'iso42001', label: 'ISO 42001' },
                { key: 'euAiAct', label: 'EU AI Act' },
                { key: 'nist', label: 'NIST AI RMF' },
                { key: 'internal', label: 'Internal Audit' },
              ].map(fw => (
                <label key={fw.key} className="flex items-center gap-2 cursor-pointer text-xs text-[#94A3B8]">
                  <input type="checkbox" checked={frameworks[fw.key]} onChange={() => toggleFramework(fw.key)} className="accent-[#6366F1] w-3.5 h-3.5" />
                  {fw.label}
                </label>
              ))}
              <div className="pt-2 mt-1 border-t border-[#1E2A45]">
                {[
                  { key: 'iso27001', label: 'ISO 27001 / 27701 mapping', tag: 'logging, access, privacy controls — additional context' },
                  { key: 'nist80053', label: 'NIST 800-53 families', tag: 'AU, AC, CM, IR — US enterprise and FedRAMP context' },
                  { key: 'nis2', label: 'NIS2', tag: 'EU incident reporting and logging context' },
                ].map(fw => (
                  <label key={fw.key} className="flex items-start gap-2 cursor-pointer text-[10px] text-[#64748B] mb-1.5">
                    <input type="checkbox" checked={extFrameworks[fw.key]} onChange={() => toggleExtFramework(fw.key)} className="accent-[#6366F1] w-3 h-3 mt-0.5" />
                    <span>{fw.label} <span className="text-[#475569]">({fw.tag})</span></span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Workflows in scope */}
          <div>
            <div className="text-[10px] text-[#475569] uppercase tracking-wider mb-2">Workflows in scope</div>
            <div className="space-y-1.5">
              {allWorkflows.map(wf => (
                <label key={wf} className="flex items-center gap-2 cursor-pointer text-xs text-[#94A3B8]">
                  <input type="checkbox" checked={workflows[wf]} onChange={() => toggleWorkflow(wf)} className="accent-[#6366F1] w-3.5 h-3.5" />
                  {wf}
                </label>
              ))}
            </div>
          </div>

          {/* Evidence completeness threshold */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] text-[#475569] uppercase tracking-wider">Evidence completeness threshold</span>
              <span className="text-xs font-mono text-[#6366F1]">{threshold}%</span>
            </div>
            <input type="range" min="50" max="100" value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} className="w-full h-1.5 rounded-full appearance-none bg-[#1E2A45] accent-[#6366F1]" />
          </div>

          <button data-testid="generate-audit-scope" onClick={() => { setScopeGenerated(true); toast.success('Audit scope generated', { description: '47,832 records in scope across 23 controls' }); }}
            className="w-full flex items-center justify-center gap-2 bg-[#6366F1] hover:bg-[#818cf8] text-white text-xs font-medium py-2.5 rounded-lg transition-all shadow-[0_0_8px_rgba(99,102,241,0.15)]">
            <Search size={13} /> Generate audit scope
          </button>

          {/* Scope metrics */}
          {scopeGenerated && (
            <div className="grid grid-cols-2 gap-2 pt-2">
              <div className="p-3 rounded-lg bg-[#080D1A] border border-[#1E2A45]"><div className="text-[9px] text-[#475569] uppercase mb-1">Records in scope</div><div className="text-lg font-bold text-[#F8FAFC]">47,832</div></div>
              <div className="p-3 rounded-lg bg-[#080D1A] border border-[#1E2A45]"><div className="text-[9px] text-[#475569] uppercase mb-1">Controls covered</div><div className="text-lg font-bold text-[#10B981]">23/31</div></div>
              <div className="p-3 rounded-lg bg-[#080D1A] border border-[#1E2A45]"><div className="text-[9px] text-[#475569] uppercase mb-1">Missing evidence</div><div className="text-lg font-bold text-[#F59E0B]">8 controls</div></div>
              <div className="p-3 rounded-lg bg-[#080D1A] border border-[#1E2A45]"><div className="text-[9px] text-[#475569] uppercase mb-1">Open exceptions</div><div className="text-lg font-bold text-[#EF4444]">3</div></div>
            </div>
          )}
        </div>

        {/* Right — Control-to-Evidence Map */}
        <div className="lg:col-span-3 glass-card overflow-hidden" data-testid="control-evidence-map">
          <div className="p-4 border-b border-[#1E2A45]">
            <h3 className="text-xs font-semibold text-[#F8FAFC] uppercase tracking-wider">Control evidence mapping</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1E2A45]">
                  {['Control ID', 'Framework', 'Description', 'Evidence Source', 'Records', 'Coverage', 'Proof Grade', 'Status'].map(h => (
                    <th key={h} className="text-left text-[9px] font-medium text-[#475569] uppercase tracking-wider py-2.5 px-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {controlEvidenceMap.map((row) => {
                  const covColor = row.coverage >= 90 ? '#10B981' : row.coverage >= 60 ? '#F59E0B' : '#EF4444';
                  return (
                    <tr key={row.id} className="border-b border-[#1E2A45]/50" data-testid={`control-${row.id}`}>
                      <td className="py-2.5 px-3 font-mono text-[#6366F1] text-xs">{row.id}</td>
                      <td className="py-2.5 px-3 text-[10px] text-[#94A3B8]">{row.framework}</td>
                      <td className="py-2.5 px-3 text-[10px] text-[#F8FAFC] max-w-[180px]">{row.desc}</td>
                      <td className="py-2.5 px-3 text-[10px] text-[#64748B]">{row.source}</td>
                      <td className="py-2.5 px-3 text-[10px] font-mono text-[#94A3B8]">{row.records}</td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-1.5">
                          <div className="w-12 h-1.5 rounded-full bg-[#1E2A45] overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${row.coverage}%`, backgroundColor: covColor }} />
                          </div>
                          <span className="text-[10px] font-mono" style={{ color: covColor }}>{row.coverage}%</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <GradeTooltip grade={row.proofGrade || 'OBSERVATIONAL'}>
                          <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-full border cursor-help ${
                            row.proofGrade === 'AUDIT-GRADE' ? 'bg-[#10B981]/10 border-[#10B981]/20 text-[#10B981]' :
                            row.proofGrade === 'OPERATIONS-GRADE' ? 'bg-[#F59E0B]/10 border-[#F59E0B]/20 text-[#F59E0B]' :
                            'bg-[#94A3B8]/10 border-[#94A3B8]/20 text-[#94A3B8]'
                          }`}>◆ {row.proofGrade?.replace('-GRADE', '') || 'N/A'}</span>
                        </GradeTooltip>
                      </td>
                      <td className="py-2.5 px-3">
                        <span className={`text-[9px] px-2 py-0.5 rounded-full ${
                          row.status === 'ready' ? 'bg-[#10B981]/10 text-[#10B981]' : row.status === 'partial' ? 'bg-[#F59E0B]/10 text-[#F59E0B]' : 'bg-[#EF4444]/10 text-[#EF4444]'
                        }`}>
                          {row.status === 'ready' ? '\u2705 Ready' : row.status === 'partial' ? '\u26A0\uFE0F Partial' : '\uD83D\uDD34 Gap'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {/* Extended framework rows */}
            {hasExtended && activeExtRows.length > 0 && (
              <div className="border-t border-[#6366F1]/20">
                <div className="px-3 py-2 text-[9px] text-[#6366F1] uppercase tracking-wider font-semibold bg-[#6366F1]/5">Extended framework mapping</div>
                <table className="w-full text-sm">
                  <tbody>
                    {activeExtRows.map((row) => {
                      const covColor = row.coverage >= 90 ? '#10B981' : row.coverage >= 60 ? '#F59E0B' : '#EF4444';
                      return (
                        <tr key={row.id} className="border-b border-[#1E2A45]/30">
                          <td className="py-2 px-3 font-mono text-[#6366F1] text-[10px]">{row.id}</td>
                          <td className="py-2 px-3 text-[9px] text-[#64748B]">{row.framework}</td>
                          <td className="py-2 px-3 text-[9px] text-[#94A3B8] max-w-[180px]">{row.desc}</td>
                          <td className="py-2 px-3 text-[9px] text-[#475569]">{row.source}</td>
                          <td className="py-2 px-3 text-[9px] font-mono text-[#64748B]">{row.records}</td>
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-1.5">
                              <div className="w-10 h-1 rounded-full bg-[#1E2A45] overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${row.coverage}%`, backgroundColor: covColor }} />
                              </div>
                              <span className="text-[9px] font-mono" style={{ color: covColor }}>{row.coverage}%</span>
                            </div>
                          </td>
                          <td className="py-2 px-3">
                            <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${row.status === 'ready' ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#F59E0B]/10 text-[#F59E0B]'}`}>
                              {row.status === 'ready' ? 'Ready' : 'Partial'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Export section */}
          <div className="p-4 border-t border-[#1E2A45]">
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid="export-population" onClick={() => { exportPopulationCSV(); toast.success('Population CSV downloaded: apex-audit-population.csv'); }}
                className="flex items-center gap-1.5 bg-[#0F1629] border border-[#1E2A45] hover:border-[#6366F1] text-[#94A3B8] text-[11px] px-3 py-2 rounded-lg transition-colors">
                <Download size={12} /> Export population (CSV)
              </button>
              <button data-testid="export-exceptions" onClick={() => { exportExceptionReportPDF(); toast.success('Exception report downloaded as PDF'); }}
                className="flex items-center gap-1.5 bg-[#0F1629] border border-[#1E2A45] hover:border-[#6366F1] text-[#94A3B8] text-[11px] px-3 py-2 rounded-lg transition-colors">
                <FileText size={12} /> Export exception report (PDF)
              </button>
              <button data-testid="export-internal-audit" onClick={() => { exportInternalAuditPackage(); toast.success('Internal audit package downloaded (PDF + CSV)'); }}
                className="flex items-center gap-1.5 bg-[#0F1629] border border-[#1E2A45] hover:border-[#6366F1] text-[#94A3B8] text-[11px] px-3 py-2 rounded-lg transition-colors">
                <FileText size={12} /> Export internal audit package (PDF + CSV)
              </button>
              <button data-testid="export-audit-package" onClick={() => { exportAuditPackageZIP(hasExtended); toast.success('Complete audit package downloaded as ZIP'); }}
                className="flex items-center gap-1.5 bg-[#6366F1] hover:bg-[#818cf8] text-white text-[11px] font-medium px-3 py-2 rounded-lg transition-all shadow-[0_0_8px_rgba(99,102,241,0.15)]">
                <Download size={12} /> Export audit package (ZIP)
              </button>
            </div>
            <p className="text-[10px] text-[#475569] mt-3 leading-relaxed">Apex provides AI decision evidence for assurance programs. Your auditor, CPA firm, or internal audit team provides the attestation, opinion, or audit finding.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EvidenceVault() {
  return (
    <div data-testid="evidence-vault">
      <Tabs defaultValue="vault-overview">
        <TabsList className="bg-transparent border-b border-[#1E2A45] rounded-none px-0 h-auto py-0 justify-start gap-0 mb-4">
          <TabsTrigger value="vault-overview" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#6366F1] data-[state=active]:text-[#6366F1] text-[#64748B] text-sm px-5 py-3 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none">Vault Overview</TabsTrigger>
          <TabsTrigger value="auditor-workspace" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#6366F1] data-[state=active]:text-[#6366F1] text-[#64748B] text-sm px-5 py-3 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none">Auditor Workspace · Chain Integrity Engine</TabsTrigger>
        </TabsList>
        <TabsContent value="vault-overview" className="mt-0">
          <VaultOverview />
        </TabsContent>
        <TabsContent value="auditor-workspace" className="mt-0">
          <AuditorWorkspace />
        </TabsContent>
      </Tabs>
    </div>
  );
}
